import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Icon from 'components/global/Icon';
import Modal from 'components/global/Modal';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ENUMS, SALES_CHANNELS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faSave } from '@fortawesome/pro-solid-svg-icons';

import { InformationHeaderContainer } from './CreateShipment/ShippingOptions.styled';

const InformationHeader = ({ title, description }) => (
	<InformationHeaderContainer>
		<Typography color="var(--black)" fontWeight="500">
			{title}
		</Typography>

		{description && (
			<Tooltip title={description}>
				<Icon icon={faCircleInfo} color="var(--content-4)" size="small" />
			</Tooltip>
		)}
	</InformationHeaderContainer>
);

const StatusMappingForm = NiceModal.create(({ credentialsID, enumValue }) => {
	const [isLoading, setIsLoading] = useState(false);
	const axiosPrivate = useAxiosPrivate();
	const modal = useModal();
	const { showError, showSuccess } = useAlert();

	const schema = useMemo(() =>
		yup.object().shape({
			statusMappings: yup.array().of(
				yup.object().shape({
					ecomStatus: yup.string().nullable(),
					otoStatus: yup.string().nullable().required(),
					updateType: yup.string().nullable().required(),
					ecomComment: yup.string().nullable()
				})
			)
		})
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			statusMappings: [
				{
					ecomStatus: null,
					otoStatus: null,
					updateType: null,
					ecomComment: null
				}
			]
		}
	});

	const {
		handleSubmit,
		setValue,
		reset,
		watch,
		control,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	const { fields, append, remove } = useFieldArray({ control, name: 'statusMappings' });

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const fillFormInEdit = async () => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(SALES_CHANNELS.GET_STATUS_MAPPING, {
				salesChannelCredentialsID: credentialsID
			});
			const { data } = response;
			reset({ statusMappings: data });
		} catch (error) {
			AppLogger.error(error);
			showError({});
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (credentialsID) {
			fillFormInEdit();
		}
	}, [credentialsID]);

	const onSubmit = (data) => {
		setIsLoading(true);
		// filter duplicate rows
		const filteredData = data.statusMappings.filter(
			(item, index) =>
				data.statusMappings.findIndex(
					(innerItem) =>
						innerItem.otoStatus === item.otoStatus &&
						innerItem.updateType === item.updateType &&
						innerItem.ecomStatus === item.ecomStatus &&
						innerItem.ecomComment === item.ecomComment
				) === index
		);

		axiosPrivate
			.post(SALES_CHANNELS.UPDATE_STATUS_MAPPING, {
				...(credentialsID && { salesChannelCredentialsID: credentialsID }),
				salesChannelCredentialsStatusMap: filteredData
			})
			.then((response) => {
				const { status } = response;
				if (status === 200) {
					showSuccess();
					// dispatch(updateOrderOtomationRules());
					modal.remove();
				} else AppLogger.error(response);
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const columns = useMemo(
		() => [
			{
				id: 'otoStatus',
				header: () => <InformationHeader title="otoStatus" description="otoStatus.description" />,
				accessor: 'otoStatus',
				size: 100,
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						required
						placeholder="selectOne"
						name={`statusMappings[${index}].otoStatus`}
						value={watch(`statusMappings[${index}].otoStatus`)}
						enumName={ENUMS.MAGENTO_SETTINGS_OTO_STATUS}
						setValue={(value) => {
							setValue(`statusMappings[${index}].otoStatus`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
					/>
				)
			},
			{
				header: () => <InformationHeader title="updateType" description="updateType.description" />,
				accessorKey: 'updateType',
				size: 100,
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						required
						placeholder="selectOne"
						name={`statusMappings[${index}].updateType`}
						value={watch(`statusMappings[${index}].updateType`)}
						enumName={enumValue ?? ENUMS.MS_UPDATE_TYPE}
						setValue={(value) => {
							setValue(`statusMappings[${index}].updateType`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
					/>
				)
			},
			{
				id: 'ecomStatus',
				header: () => <InformationHeader title="ecomStatus" description="ecomStatus.description" />,
				accessor: 'ecomStatus',
				size: 100,
				renderCell: ({ row: { index } }) => <StringInput name={`statusMappings[${index}].ecomStatus`} />
			},
			{
				accessorKey: 'ecomComment',
				header: () => <InformationHeader title="ecomComment" description="ecomComment.description" />,
				size: 100,
				renderCell: ({ row: { index } }) => <StringInput name={`statusMappings[${index}].ecomComment`} />
			},
			{
				id: 'deleteRow',
				size: 20,
				maxSize: 20
			}
		],
		[]
	);

	return (
		<Modal fullwidth className="modal-smsForm">
			<ModalTitle>
				<Typography variant="p1">statusMapping</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<Table
						loading={isLoading}
						data={fields}
						columns={columns}
						onRowDelete={(row) => {
							remove(row.index);
						}}
						onRowAdd={() => {
							append({});
						}}
						showAddNewRowButton
						initEditMode={(row) => row.id === null}
					/>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						if (isDirty && !isSubmitSuccessful && !isSubmitting) {
							NiceModal.show(ConfirmationModal, {
								yesAction: () => {
									modal.remove();
								}
							});
						} else {
							modal.remove();
						}
					}}
					variant="outline"
				/>
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faSave}
					disabled={!isDirty}
					label="save"
					loading={isLoading}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default StatusMappingForm;
